import { useContext, useEffect, useState } from "react"
import { UserContext } from "../../context/userContext"
import { useLazyQuery } from "@apollo/client"
import { Link } from "react-router-dom"
import { CUSTOM_FORMS_BY_PARTICIPANT } from "./queries" // Update this import to your actual query
import AppBreadcrumb from "../../components/AppBreadcrumb/AppBreadcrumb"
import customStyles from "../../styles/customDataForms/CustomDataForms.module.css"
import _ from "lodash"
import LoaderFullPage from "../../components/Loaders"
import CustomTable from "../../components/CustomTable/CustomTable"
import { formatToLocalDate } from "../../helpers/dateConversion"
import { LogError } from "../../helpers/logger"
import StandardPageHeader from "../../components/StandardPageHeader/StandardPageHeader"

export default function CustomsFormsIndex() {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(null)
  const [organizationFilter, setOrganizationFilter] = useState(null)
  const [locationFilter, setLocationFilter] = useState(null)
  const [formsFilter, setFormsFilter] = useState(null)
  const userCtx = useContext(UserContext)
  const [staffFilter, setStaffFilter] = useState(null)
  const ID_TEMPLATES = [
    "assessment_8_dimensions",
    "hep_ab_form",
    "hep_c_form",
    "housing_assistance_grant_award",
    "form_insurance_status",
    "lyft_assessment",
    "real_d_form",
  ]

  const mapOrganization = (organization) => {
    if (!organization) {
      return "Unknown Organization"
    }
    if (organization === false) {
      return "Unknown Organization"
    }
    return organization
  }

  const [getParticipantForms] = useLazyQuery(CUSTOM_FORMS_BY_PARTICIPANT, {
    variables: {
      participant_id: userCtx.participant_id,
      idTemplates: ID_TEMPLATES,
      participant_membership_id: "", // Query appears to require this, but it is not used in the sever function
    },
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      if (data.customForm === null) {
        setData(null)
        setLoading(false)
        return
      }
      const dataFormatted = JSON.parse(data.customForm.data)
      const handleData = () => {
        const validTemplates = _.filter(dataFormatted, (template) => {
          const blocks = _.groupBy(template, "block_name")
          return !_.isEmpty(blocks.form)
        })

        return _.map(validTemplates, (template) => {
          const blocks = _.groupBy(template, "block_name")
          const session = blocks.form[0]
          const staffID = session.staff_id
          const handleLocations = () => {
            const locationsList = _.map(
              session?.values?.form__location_ids,
              ({ label }) => label,
            )
            return _.join(locationsList, ", ")
          }
          return {
            organization: mapOrganization(session?.organization_name),
            date: new Date(session?.values?.form_date),
            locations: handleLocations(),
            form: getLabelById(session?.idTemplate),
            formDuration: `${session?.values?.form_length} minutes`,
            facilitating_staff: _.unescape(session?.staff_full_name),
            view: (
              <Link
                to={`/form/view/${session?.organization_id}/${session?.idTemplate}/${session?.idStamp}`}
                state={{
                  staffId: staffID,
                  previousTitle: "Custom Forms",
                  previousRoute: "/participant/custom-forms",
                }}
                className={customStyles.actionButton}
              >
                View
              </Link>
            ),
          }
        })
      }
      setData(handleData())
      setLoading(false)
      const allLocations = _.map(handleData(), ({ locations }) => locations)
      const allOrganizations = _.map(
        handleData(),
        ({ organization }) => organization,
      )
      const allForms = _.map(handleData(), ({ form }) => form)
      const allStaffs = _.map(
        handleData(),
        ({ facilitating_staff }) => facilitating_staff,
      )
      setFormsFilter(_.uniq(allForms).sort())
      setOrganizationFilter(_.uniq(allOrganizations).sort())
      setLocationFilter(_.uniq(allLocations).sort())
      setStaffFilter(_.uniq(allStaffs).sort())
    },
    onError: (error) => {
      LogError(error)
    },
  })

  useEffect(() => {
    getParticipantForms()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const formsLabels = [
    { id: "hep_ab_form", label: "HEP AB Form" },
    { id: "hep_c_form", label: "HEP C Form" },
  ]

  const getLabelById = (id) => {
    const item = formsLabels.find((item) => item.id === id)
    return item ? item.label : _.startCase(id?.replace(/_/g, " "))
  }

  const filters = () => {
    return [
      {
        value: "organization",
        label: "Organization",
        options: organizationFilter,
        placeholder: "Select organization",
        showClear: true,
        type: "multiselect",
      },
      {
        value: "location",
        label: "Location",
        options: locationFilter,
        placeholder: "Select location",
        showClear: true,
        type: "dropdown",
      },
      {
        value: "form",
        label: "Form",
        options: formsFilter,
        placeholder: "Select form",
        showClear: true,
        type: "dropdown",
      },
      {
        value: "engagementDate",
        label: "Engagement date",
        placeholder: "Select",
        showClear: true,
        type: "calendar",
      },
      {
        value: "staff",
        label: "Staff",
        placeholder: "Select staff",
        showClear: true,
        type: "multiselect",
        options: staffFilter,
      },
    ]
  }

  const sorting = () => {
    return [
      {
        value: "sortBy",
        label: "Sort by",
        options: [
          { name: "Engagement date", code: "engagementDate" },
          { name: "Staff name", code: "staff" },
          { name: "Organization", code: "organization" },
        ],
        default: "engagementDate",
        placeholder: "Select option",
        showClear: true,
        type: "multiselect",
      },
    ]
  }

  const headers = () => {
    return [
      {
        value: "engagementDate",
        label: "Engagement Date",
        size: 2,
      },
      {
        value: "form",
        label: "Form",
        size: 2,
      },
      {
        value: "duration",
        label: "Duration",
        size: 2,
      },
      {
        value: "staff",
        label: "Staff",
        size: 2,
      },
      {
        value: "organization",
        label: "Organization",
        size: 2,
      },
      {
        value: "location",
        label: "Location",
        size: 1,
      },
      {
        value: "view",
        label: "",
        action: true,
        size: 1,
      },
    ]
  }

  const mapLocation = (location) => {
    if (!location) {
      return "Not set"
    }
    if (location === false) {
      return "Not set"
    }
    return location
  }

  const mapData = () => {
    return data?.map((d) => {
      return {
        organization: d.organization,
        location: mapLocation(d.locations),
        form: d.form,
        engagementDate: formatToLocalDate(d.date),
        duration: d.formDuration,
        staff: d.facilitating_staff,
        view: d.view,
      }
    })
  }

  return (
    <div className="main-content">
      <AppBreadcrumb items={[{ label: "Custom Forms" }]} />
      <div className="w-full pb-5">
        <StandardPageHeader
          title="Custom Forms"
          refreshData={() => {
            setLoading(true)
            getParticipantForms()
          }}
        />

        <hr className="line" />

        {loading ? (
          <LoaderFullPage />
        ) : (
          <CustomTable
            headers={headers()}
            collection={mapData()}
            filters={filters()}
            sorting={sorting()}
          />
        )}
      </div>
    </div>
  )
}
